import React, { useState, useRef, useEffect } from 'react';
import {
  Fab, Dialog, DialogTitle, DialogContent, Box, TextField,
  IconButton, Typography, Paper, CircularProgress, Slide
} from '@mui/material';
import {
  SmartToy as BotIcon, Send as SendIcon, Close as CloseIcon,
  AutoAwesome as AIIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import apiService from '../utils/api';
import useRoadmapStore from '../store/roadmapStore';

export default function AIChatWidget() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const currentRoadmapId = useRoadmapStore(s => s.currentRoadmapId);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const { reply } = await apiService.aiChat(
        newMessages.map(m => ({ role: m.role, content: m.content })),
        currentRoadmapId,
        i18n.language
      );
      setMessages([...newMessages, { role: 'assistant', content: reply }]);
    } catch {
      setMessages([...newMessages, { role: 'assistant', content: t('ai.chatError') }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
          background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
          color: '#fff',
          '&:hover': { background: 'linear-gradient(135deg, #6d28d9 0%, #4f46e5 100%)' },
          boxShadow: '0 4px 20px rgba(124,58,237,0.4)'
        }}
      >
        <BotIcon />
      </Fab>

      {/* Chat Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            position: 'fixed', bottom: 16, right: 16, m: 0,
            width: { xs: 'calc(100% - 32px)', sm: 400 },
            maxHeight: '70vh', borderRadius: 3,
            background: 'var(--dialog-bg)'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AIIcon sx={{ color: 'var(--ai-accent)' }} />
            <Typography variant="subtitle1" fontWeight={700}>{t('ai.chatTitle')}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
          {/* Messages Area */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1, minHeight: 300, maxHeight: '50vh' }}>
            {messages.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4, opacity: 0.6 }}>
                <BotIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="body2">{t('ai.chatWelcome')}</Typography>
              </Box>
            )}
            {messages.map((msg, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start', mb: 1 }}>
                <Paper sx={{
                  px: 2, py: 1, maxWidth: '85%', borderRadius: 2,
                  bgcolor: msg.role === 'user' ? 'rgba(99,102,241,0.2)' : 'rgba(168,85,247,0.1)',
                  border: '1px solid',
                  borderColor: msg.role === 'user' ? 'rgba(99,102,241,0.3)' : 'rgba(168,85,247,0.2)'
                }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
                </Paper>
              </Box>
            ))}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                <Paper sx={{ px: 2, py: 1, borderRadius: 2, bgcolor: 'rgba(168,85,247,0.1)' }}>
                  <CircularProgress size={16} sx={{ color: 'var(--ai-accent)' }} />
                </Paper>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box sx={{ p: 1.5, borderTop: '1px solid var(--track)', display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              size="small"
              placeholder={t('ai.chatPlaceholder')}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
            />
            <IconButton onClick={sendMessage} disabled={!input.trim() || loading}
              sx={{ color: 'var(--ai-accent)', '&:hover': { bgcolor: 'rgba(168,85,247,0.1)' } }}>
              <SendIcon />
            </IconButton>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
